import { useTranslation } from "react-i18next";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";

type ReadingCounts = {
	meter_readings_count: number;
	cnt_completed: number;
	cnt_error: number;
	cnt_no_communication: number;
	cnt_request_sent: number;
};

const SEGMENTS = [
	{ key: "cnt_completed", color: "bg-emerald-500" },
	{ key: "cnt_error", color: "bg-red-500" },
	{ key: "cnt_no_communication", color: "bg-amber-500" },
	{ key: "cnt_request_sent", color: "bg-blue-400" },
] as const;

export function ReadingProgressBar({ reading }: { reading: ReadingCounts }) {
	const { t } = useTranslation();
	const total = reading.meter_readings_count;

	if (total === 0) {
		return <span className="text-xs text-muted-foreground">0</span>;
	}

	const labels: Record<string, string> = {
		cnt_completed: t("mqttReadingsTable.cntCompleted"),
		cnt_error: t("mqttReadingsTable.cntError"),
		cnt_no_communication: t("mqttReadingsTable.cntNoCommunication"),
		cnt_request_sent: t("mqttReadingsTable.cntRequestSent"),
	};

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="flex w-28 flex-col gap-1">
					<div className="flex h-2 overflow-hidden rounded-full bg-muted">
						{SEGMENTS.map(({ key, color }) => {
							const value = reading[key];
							if (value === 0) return null;
							const pct = (value / total) * 100;
							return (
								<div
									key={key}
									className={`${color} transition-[width]`}
									style={{ width: `${pct}%` }}
								/>
							);
						})}
					</div>
					<span className="text-xs tabular-nums text-muted-foreground">
						{reading.cnt_completed}/{total}
					</span>
				</div>
			</TooltipTrigger>
			<TooltipContent side="bottom" className="text-xs">
				<div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
					{SEGMENTS.map(({ key, color }) => (
						<div key={key} className="contents">
							<div className="flex items-center gap-1.5">
								<span className={`inline-block size-2 rounded-full ${color}`} />
								<span>{labels[key]}</span>
							</div>
							<span className="text-right tabular-nums font-medium">
								{reading[key]}
							</span>
						</div>
					))}
				</div>
			</TooltipContent>
		</Tooltip>
	);
}
